import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, AlertTriangle, ChevronDown, ChevronUp, Send, UserCheck,
  XCircle, CheckCircle2, Loader2, Link2, Copy, RefreshCw, Trash2,
  ExternalLink, Sparkles, DollarSign, Briefcase, Eye, UserX, Phone, Award,
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import CandidateMiniCard from '../../components/admin/CandidateMiniCard';
import TimelineActivity from '../../components/admin/TimelineActivity';
import MissionSmartMatchesPanel from '../../components/admin/MissionSmartMatchesPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/* ============================================================
   PIPELINE SECTIONS — aligned with new business flow
   ============================================================ */
const PIPELINE_SECTIONS = [
  { key: 'applied', label: 'Candidatures', color: 'border-gray-300 bg-gray-50/40', dotColor: 'bg-gray-400', countBg: 'bg-gray-200 text-gray-600', icon: Users },
  { key: 'preselected', label: 'Présélectionnés', color: 'border-amber-300 bg-amber-50/40', dotColor: 'bg-amber-400', countBg: 'bg-amber-100 text-amber-700', icon: Eye },
  { key: 'shortlisted', label: 'Shortlistés', color: 'border-orange-300 bg-orange-50/40', dotColor: 'bg-orange-400', countBg: 'bg-orange-100 text-orange-700', icon: Award },
  { key: 'presented_to_client', label: 'Présentés au client', color: 'border-purple-300 bg-purple-50/40', dotColor: 'bg-purple-500', countBg: 'bg-purple-100 text-purple-700', icon: Send },
  { key: 'client_interested', label: 'Client intéressé', color: 'border-blue-300 bg-blue-50/40', dotColor: 'bg-blue-500', countBg: 'bg-blue-100 text-blue-700', icon: UserCheck },
  { key: 'interview_done', label: 'Entretien réalisé', color: 'border-indigo-300 bg-indigo-50/40', dotColor: 'bg-indigo-500', countBg: 'bg-indigo-100 text-indigo-700', icon: Phone },
];

const FINAL_SECTION = { key: 'finalized', label: 'Finalisés', color: 'border-gray-300 bg-gray-50/40', dotColor: 'bg-gray-400', countBg: 'bg-gray-200 text-gray-600', icon: CheckCircle2 };
const FINAL_STATUSES = ['hired', 'client_not_interested', 'rejected_after_interview', 'rejected'];

const FINAL_LABELS = {
  hired: { label: 'Embauché', cls: 'bg-green-100 text-green-700' },
  client_not_interested: { label: 'Client non intéressé', cls: 'bg-gray-200 text-gray-600' },
  rejected_after_interview: { label: 'Refusé après entretien', cls: 'bg-red-100 text-red-600' },
  rejected: { label: 'Rejeté', cls: 'bg-red-50 text-red-500' },
};

/* ============================================================
   ACTIONS PAR STATUS
   ============================================================ */
const getActionsForStatus = (status, limitReached) => {
  switch (status) {
    case 'applied':
      return [
        { id: 'preselect', label: 'Présélectionner', targetStatus: 'preselected', variant: 'primary' },
        { id: 'reject', label: 'Rejeter', targetStatus: 'rejected', variant: 'danger' },
      ];
    case 'preselected':
      return [
        { id: 'shortlist', label: 'Shortlister', targetStatus: 'shortlisted', variant: 'primary' },
        { id: 'reject', label: 'Rejeter', targetStatus: 'rejected', variant: 'danger' },
      ];
    case 'shortlisted':
      return [
        { id: 'present', label: 'Présenter au client', targetStatus: 'presented_to_client', variant: 'primary', disabled: limitReached },
        { id: 'reject', label: 'Rejeter', targetStatus: 'rejected', variant: 'danger' },
      ];
    case 'presented_to_client':
      return []; // Client decides
    case 'client_interested':
      return [
        { id: 'interview_done', label: 'Entretien réalisé', targetStatus: 'interview_done', variant: 'primary' },
        { id: 'reject', label: 'Refuser', targetStatus: 'rejected_after_interview', variant: 'danger' },
      ];
    case 'interview_done':
      return [
        { id: 'hire', label: 'Embauché', targetStatus: 'hired', variant: 'success' },
        { id: 'reject', label: 'Refusé', targetStatus: 'rejected_after_interview', variant: 'danger' },
      ];
    default:
      return [];
  }
};

/* ============================================================
   MAIN PAGE
   ============================================================ */
const MissionDetailsPage = () => {
  const { missionId } = useParams();
  const navigate = useNavigate();

  const [mission, setMission] = useState(null);
  const [shortlistData, setShortlistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [clientLink, setClientLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [scoringLoading, setScoringLoading] = useState(false);
  const [pricing, setPricing] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, sRes, lRes, pRes] = await Promise.all([
        api.get(`/admin/missions/${missionId}`),
        api.get(`/shortlist/mission/${missionId}`),
        api.get(`/client-portal/admin/missions/${missionId}/client-link`),
        api.get(`/shortlist/mission/${missionId}/pricing`),
      ]);
      setMission(await mRes.json());
      setShortlistData(await sRes.json());
      setClientLink(await lRes.json());
      if (pRes.ok) setPricing(await pRes.json());
    } catch (e) {
      console.error('Load failed:', e);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [missionId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleStatusChange = async (entryId, newStatus) => {
    setUpdatingId(entryId);
    try {
      const res = await api.patch(`/shortlist/${entryId}`, { status: newStatus });
      if (res.ok) {
        toast.success('Statut mis à jour');
        await fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (e) { toast.error('Erreur de connexion'); }
    finally { setUpdatingId(null); }
  };

  const toggleSection = (key) => setCollapsedSections(p => ({ ...p, [key]: !p[key] }));

  const generateClientLink = async (hours = 168) => {
    setLinkLoading(true);
    try {
      const res = await api.post(`/client-portal/admin/missions/${missionId}/client-link`, { expires_in_hours: hours });
      if (res.ok) { toast.success('Lien client généré'); await fetchAll(); }
    } catch (e) { toast.error('Erreur'); }
    finally { setLinkLoading(false); }
  };

  const revokeClientLink = async () => {
    if (!window.confirm('Révoquer le lien client ?')) return;
    setLinkLoading(true);
    try {
      const res = await api.delete(`/client-portal/admin/missions/${missionId}/client-link`);
      if (res.ok) { toast.success('Lien révoqué'); await fetchAll(); }
    } catch (e) { toast.error('Erreur'); }
    finally { setLinkLoading(false); }
  };

  const copyClientLink = () => {
    if (clientLink?.token) {
      navigator.clipboard.writeText(`${BACKEND_URL}/client/m/${clientLink.token}`);
      toast.success('Lien copié');
    }
  };

  const triggerScoring = async () => {
    setScoringLoading(true);
    try {
      const res = await api.post(`/shortlist/mission/${missionId}/score`);
      if (res.ok) { const d = await res.json(); toast.success(`${d.scored} candidat(s) scoré(s)`); await fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur scoring'); }
    } catch (e) { toast.error('Erreur'); }
    finally { setScoringLoading(false); }
  };

  /* Derived data */
  const entries = shortlistData?.shortlist || [];
  const counts = shortlistData?.counts || {};
  const presentedLimit = shortlistData?.presented_limit || 2;
  const presentedRemaining = shortlistData?.presented_slots_remaining ?? 2;
  const limitReached = presentedRemaining <= 0;

  const entriesByStatus = {};
  PIPELINE_SECTIONS.forEach(s => { entriesByStatus[s.key] = entries.filter(e => e.status === s.key); });
  const finalEntries = entries.filter(e => FINAL_STATUSES.includes(e.status));

  if (loading) {
    return (
      <div className="p-4 sm:p-8" data-testid="mission-details-page">
        {[1, 2, 3].map(i => <div key={`skeleton-${i}`} className="h-24 bg-gray-100 rounded-lg mb-3 animate-pulse" />)}
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="p-4 sm:p-8 text-center" data-testid="mission-details-page">
        <Briefcase className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Mission introuvable</p>
        <button onClick={() => navigate('/admin/missions')} className="mt-4 text-sm text-tp-primary underline">Retour</button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto" data-testid="mission-details-page">
      {/* HEADER */}
      <div className="mb-6">
        <button onClick={() => navigate('/admin/missions')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-tp-primary mb-3" data-testid="back-to-missions-button">
          <ArrowLeft className="h-4 w-4" /> Missions
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-display font-semibold text-gray-900" data-testid="mission-title">{mission.role_required}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{mission.client_name || mission.client?.company_name || 'Client'}{mission.seniority_level && ` · ${mission.seniority_level}`}</p>
          </div>
          <StatusBadge status={mission.status} />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-2 mb-4" data-testid="pipeline-kpis">
        <KPICard label="Pipeline" value={counts.total || 0} sub="candidats" />
        <KPICard label="Shortlistés" value={counts.shortlisted || 0} sub="prêts" />
        <KPICard label="Présentés" value={`${counts.presented_to_client || 0}/${presentedLimit}`} sub={limitReached ? 'LIMITE' : `${presentedRemaining} place(s)`} alert={limitReached} />
        <KPICard label="Embauchés" value={counts.hired || 0} sub="recrutés" success={(counts.hired || 0) > 0} />
      </div>

      {/* ACTIONS */}
      {(counts.total || 0) > 0 && (
        <div className="flex items-center gap-2 mb-5">
          <button onClick={triggerScoring} disabled={scoringLoading} className="flex items-center gap-1.5 text-xs font-medium bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50" data-testid="trigger-scoring-btn">
            {scoringLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3 text-amber-500" />}
            {scoringLoading ? 'Scoring...' : 'Scorer (IA)'}
          </button>
        </div>
      )}

      {/* LIMIT BANNER */}
      {limitReached && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-5 flex items-start gap-2" data-testid="presented-limit-banner">
          <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-orange-700">Limite atteinte : maximum {presentedLimit} candidats présentés au client.</p>
        </div>
      )}

      {/* CLIENT LINK */}
      <ClientLinkSection clientLink={clientLink} linkLoading={linkLoading} onGenerate={generateClientLink} onRevoke={revokeClientLink} onCopy={copyClientLink} backendUrl={BACKEND_URL} />

      {/* PIPELINE SECTIONS */}
      <div className="space-y-3 mb-6" data-testid="pipeline-sections">
        {PIPELINE_SECTIONS.map(section => {
          const sectionEntries = entriesByStatus[section.key] || [];
          const count = sectionEntries.length;
          const isCollapsed = collapsedSections[section.key];
          const Icon = section.icon;

          if (count === 0 && ['applied'].includes(section.key)) {
            return <CollapsedEmpty key={section.key} section={section} onClick={() => toggleSection(section.key)} />;
          }

          return (
            <div key={section.key} className={`rounded-lg border ${section.color} overflow-hidden`} data-testid={`pipeline-section-${section.key}`}>
              <button onClick={() => toggleSection(section.key)} className="w-full flex items-center gap-2 p-3 text-left" data-testid={`section-toggle-${section.key}`}>
                <div className={`w-2 h-2 rounded-full ${section.dotColor}`} />
                <Icon className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-semibold text-gray-800 flex-1">{section.label}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${section.countBg}`}>{count}</span>
                {section.key === 'presented_to_client' && <span className="text-xs text-gray-400">({counts.presented_to_client || 0}/{presentedLimit})</span>}
                {isCollapsed ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronUp className="h-4 w-4 text-gray-400" />}
              </button>
              {!isCollapsed && (
                <div className="px-3 pb-3 space-y-2">
                  {count === 0 ? (
                    <p className="text-xs text-gray-400 italic py-2 pl-4">Aucun candidat dans cette étape.</p>
                  ) : sectionEntries.map(entry => (
                    <CandidateMiniCard
                      key={entry.id}
                      candidate={{ id: entry.id, first_name: entry.candidate?.first_name || '-', last_name: entry.candidate?.last_name || '', main_profession: entry.candidate?.main_profession, country: entry.candidate?.country, expected_salary: entry.candidate?.expected_salary }}
                      status={entry.status}
                      score={entry.ai_score}
                      aiBreakdown={entry.ai_breakdown}
                      aiResume={entry.ai_resume}
                      actions={getActionsForStatus(entry.status, limitReached).map(a => ({ ...a, onClick: () => handleStatusChange(entry.id, a.targetStatus) }))}
                      disabled={updatingId === entry.id}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* FINALIZED SECTION */}
        <div className={`rounded-lg border ${FINAL_SECTION.color} overflow-hidden`} data-testid="pipeline-section-finalized">
          <button onClick={() => toggleSection('finalized')} className="w-full flex items-center gap-2 p-3 text-left" data-testid="section-toggle-finalized">
            <div className={`w-2 h-2 rounded-full ${FINAL_SECTION.dotColor}`} />
            <FINAL_SECTION.icon className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-semibold text-gray-800 flex-1">{FINAL_SECTION.label}</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${FINAL_SECTION.countBg}`}>{finalEntries.length}</span>
            {collapsedSections['finalized'] ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronUp className="h-4 w-4 text-gray-400" />}
          </button>
          {!collapsedSections['finalized'] && (
            <div className="px-3 pb-3 space-y-2">
              {finalEntries.length === 0 ? (
                <p className="text-xs text-gray-400 italic py-2 pl-4">Aucun candidat finalisé.</p>
              ) : finalEntries.map(entry => {
                const fl = FINAL_LABELS[entry.status] || { label: entry.status, cls: 'bg-gray-100 text-gray-600' };
                return (
                  <div key={entry.id} className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3" data-testid={`final-card-${entry.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{entry.candidate?.first_name} {entry.candidate?.last_name}</p>
                      <p className="text-xs text-gray-500 truncate">{entry.candidate?.main_profession || '-'}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${fl.cls}`}>{fl.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* PRICING */}
      {pricing && (pricing.salary_data?.client_price > 0 || pricing.mission_budget > 0) && <PricingSection pricing={pricing} />}

      {/* TIMELINE */}
      <div className="border-t pt-4" data-testid="mission-timeline-section">
        <MissionSmartMatchesPanel missionId={missionId} />
        <TimelineActivity missionId={missionId} maxItems={20} />
      </div>
    </div>
  );
};

/* ============================================================
   SUB-COMPONENTS
   ============================================================ */
const StatusBadge = ({ status }) => {
  const c = { open: { l: 'Ouverte', c: 'bg-sky-100 text-sky-700' }, active: { l: 'Active', c: 'bg-green-100 text-green-700' }, assigned: { l: 'Assignée', c: 'bg-purple-100 text-purple-700' }, completed: { l: 'Terminée', c: 'bg-gray-200 text-gray-600' } }[status] || { l: status, c: 'bg-gray-100 text-gray-600' };
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${c.c}`} data-testid="mission-status-badge">{c.l}</span>;
};

const KPICard = ({ label, value, sub, alert = false, success = false }) => (
  <div className={`rounded-lg border p-3 text-center ${alert ? 'border-orange-300 bg-orange-50' : success ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'}`} data-testid={`kpi-${label.toLowerCase().replace(/[\s/]/g, '-')}`}>
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className={`text-lg font-bold ${alert ? 'text-orange-600' : success ? 'text-green-600' : 'text-gray-900'}`}>{value}</p>
    <p className={`text-xs ${alert ? 'text-orange-500 font-semibold' : 'text-gray-400'}`}>{sub}</p>
  </div>
);

const CollapsedEmpty = ({ section, onClick }) => (
  <button onClick={onClick} className="w-full flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50/50 text-left opacity-60 hover:opacity-80" data-testid={`pipeline-section-${section.key}`}>
    <div className={`w-2 h-2 rounded-full ${section.dotColor}`} />
    <section.icon className="h-4 w-4 text-gray-400" />
    <span className="text-sm font-medium text-gray-500">{section.label}</span>
    <span className="text-xs text-gray-400 ml-auto">0</span>
  </button>
);

/* CLIENT LINK SECTION */
const EXPIRY_OPTIONS = [
  { label: '24h', hours: 24 }, { label: '7 jours', hours: 168 }, { label: '14 jours', hours: 336 }, { label: '30 jours', hours: 720 },
];

const ClientLinkSection = ({ clientLink, linkLoading, onGenerate, onRevoke, onCopy, backendUrl }) => {
  const [selectedExpiry, setSelectedExpiry] = useState(168);
  const hasLink = clientLink?.has_link && clientLink?.status === 'active';
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 mb-5" data-testid="client-link-section">
      <div className="flex items-center gap-2 mb-3">
        <Link2 className="h-4 w-4 text-tp-primary" />
        <h3 className="text-sm font-semibold text-gray-800">Lien client sécurisé</h3>
      </div>
      {hasLink ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700" data-testid="link-status-badge"><CheckCircle2 className="h-3 w-3" /> Actif</span>
            <span className="text-xs text-gray-400">Expire le {fmt(clientLink.expires_at)}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>Ouvertures : <strong>{clientLink.open_count || 0}</strong></span>
            {clientLink.last_used_at && <span>Dernier accès : {fmt(clientLink.last_used_at)}</span>}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-50 border rounded px-3 py-2 text-xs text-gray-600 truncate font-mono" data-testid="client-link-url">{backendUrl}/client/m/{clientLink.token}</div>
            <button onClick={onCopy} className="flex items-center gap-1 text-xs font-medium text-tp-primary px-2 py-2 rounded border border-gray-200 hover:bg-gray-50" data-testid="copy-client-link-btn"><Copy className="h-3.5 w-3.5" /> Copier</button>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={() => onGenerate(selectedExpiry)} disabled={linkLoading} className="flex items-center gap-1 text-xs font-medium text-gray-600 px-2 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50" data-testid="regenerate-link-btn"><RefreshCw className="h-3 w-3" /> Régénérer</button>
            <button onClick={onRevoke} disabled={linkLoading} className="flex items-center gap-1 text-xs font-medium text-red-500 px-2 py-1.5 rounded border border-red-200 hover:bg-red-50 disabled:opacity-50" data-testid="revoke-link-btn"><Trash2 className="h-3 w-3" /> Révoquer</button>
            <a href={`${backendUrl}/client/m/${clientLink.token}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs font-medium text-gray-500 px-2 py-1.5 rounded border border-gray-200 hover:bg-gray-50 ml-auto" data-testid="preview-link-btn"><ExternalLink className="h-3 w-3" /> Aperçu</a>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Aucun lien actif. Générez un lien pour que le client puisse voir les candidats présentés.</p>
          <div className="flex items-center gap-2">
            <select value={selectedExpiry} onChange={(e) => setSelectedExpiry(Number(e.target.value))} className="text-xs border rounded px-2 py-1.5 text-gray-700 bg-white" data-testid="expiry-select">
              {EXPIRY_OPTIONS.map(o => <option key={o.hours} value={o.hours}>{o.label}</option>)}
            </select>
            <button onClick={() => onGenerate(selectedExpiry)} disabled={linkLoading} className="flex items-center gap-1.5 text-xs font-medium bg-tp-primary text-white px-3 py-1.5 rounded hover:bg-tp-primary-light disabled:opacity-50" data-testid="generate-link-btn">
              {linkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />} Générer le lien
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* PRICING SECTION */
const PricingSection = ({ pricing }) => {
  const fmt = (v) => v ? `${Math.round(v).toLocaleString('fr-FR')}€` : '-';
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 mb-6" data-testid="pricing-section">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className="h-4 w-4 text-tp-primary" />
        <h3 className="text-sm font-semibold text-gray-800">Pricing mission</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        <div><p className="text-xs text-gray-500">Salaire moyen</p><p className="text-sm font-bold text-gray-800">{fmt(pricing.salary_data?.avg_expected_salary)}</p><p className="text-xs text-gray-400">{pricing.salary_data?.salaries_count || 0} candidat(s)</p></div>
        <div><p className="text-xs text-gray-500">Coût employeur</p><p className="text-sm font-bold text-gray-800">{fmt(pricing.salary_data?.employer_cost)}</p><p className="text-xs text-gray-400">+{pricing.config?.employer_charges_percent}%</p></div>
        <div><p className="text-xs text-gray-500">Prix client</p><p className="text-sm font-bold text-tp-primary">{fmt(pricing.salary_data?.client_price)}</p><p className="text-xs text-gray-400">+{pricing.config?.company_margin_percent}%</p></div>
        <div><p className="text-xs text-gray-500">Budget mission</p><p className="text-sm font-bold text-gray-800">{fmt(pricing.mission_budget)}</p>{pricing.actual_margin && <p className={`text-xs font-medium ${pricing.actual_margin.percent >= 20 ? 'text-green-600' : pricing.actual_margin.percent >= 10 ? 'text-orange-500' : 'text-red-500'}`}>Marge {Math.round(pricing.actual_margin.percent)}%</p>}</div>
      </div>
      {pricing.benchmark?.data_available && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Benchmark salarial (Madagascar)</p>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-500">Min: <strong>{fmt(pricing.benchmark?.benchmark_eur?.min)}</strong></span>
            <span className="text-gray-500">Moy: <strong>{fmt(pricing.benchmark?.benchmark_eur?.median)}</strong></span>
            <span className="text-gray-500">Max: <strong>{fmt(pricing.benchmark?.benchmark_eur?.max)}</strong></span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionDetailsPage;
